import { useMutation } from '@apollo/client'
import { NavPromptMessageProps } from '@deltek/specification-client/core/Forms/NavPrompt'
import NavPromptDialogContainer from '@deltek/specification-client/core/Forms/NavPromptDialogContainer'
import { ListingDetail } from '@deltek/specification-client/core/Models/listing'
import {
    BulkCopyListings,
    BulkDeleteListings,
    BulkMoveListings,
} from 'bpm/api/graphQL/mutations/bulkActions.mutation'
import { CopyProductForm } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/CopyDelete/CopyProduct'
import CopyMoveDialog from 'bpm/pages/ProductsPage/ProductDetailsPage/components/CopyMoveDialog'
import { ListingHeaderVariants } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingDetailsPage'
import { blankForm } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingHeader.constants'
import { AddNotesModal } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/AddNotesModal'
import usePDNhook from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/hooks/usePDNHook'
import {
    noteReasonLabels,
    typeAction,
} from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.constants'
import { resetPDNStore } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingNotes/ListingNotes.store'
import { NoteType } from 'models/projectNote'
import React, { FC, useCallback, useEffect, useState } from 'react'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { ListingPageStoreType, useListingPageStore } from '../store'

type BulkCopyMoveDialogProps = {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    type: string
    setType: (type: string) => void
}

const BulkCopyMoveDialog: FC<BulkCopyMoveDialogProps> = ({
    isOpen,
    setIsOpen,
    type,
    setType,
}) => {
    const copyMoveParams = useListingPageStore(
        (state: ListingPageStoreType) => state.copyMoveParams
    )

    const setBulkActionProgress = useListingPageStore(
        (state) => state.setBulkActionProgress
    )

    const { handleSaveNote, isPDNModalOpen, setIsPDNModalOpen } = usePDNhook()
    const { showGlobalSnackbar } = useGlobalSnackbar()

    const [isCopyMoveModalOpen, setIsCopyMoveModalOpen] = useState(false)

    const [tempPDNStore, setTempPDNStore] = useState<{
        actionType: string | keyof typeof noteReasonLabels
        value: [ListingHeaderVariants | string, string | boolean] | never[]
    }>({
        actionType: '',
        value: [],
    })

    const [copyMoveProductForm, setCopyMoveProductForm] = useState<
        Partial<CopyProductForm>
    >({ ...blankForm })

    const [bulkCopyProducts, { loading: loadingCopyProduct }] = useMutation(
        BulkCopyListings,
        {
            onCompleted: (data: any) => {
                resetPDNStore()
                setCopyMoveProductForm({ ...blankForm })

                const instanceId =
                    data?.startBulkCopyProductListingDetails?.instanceId

                if (!!instanceId) {
                    setBulkActionProgress({ copy: instanceId })
                }

                setIsCopyMoveModalOpen(false)
                setIsOpen(false)
                setType('')
            },
            onError: (error) => {
                setType('')
                showGlobalSnackbar({
                    variables: {
                        message:
                            error?.message ||
                            'Error on Copying Product Listing/s',
                        variant: 'error',
                        id: `ProductCardManagementTable_BulkCopyProductListingError`,
                    },
                })
            },
        }
    )

    const [bulkMoveProducts, { loading: loadingMoveProduct }] = useMutation(
        BulkMoveListings,
        {
            onCompleted: (data) => {
                resetPDNStore()
                setCopyMoveProductForm({ ...blankForm })

                const instanceId =
                    data?.startBulkMoveProductListingDetails?.instanceId

                if (!!instanceId) {
                    setBulkActionProgress({ move: instanceId })
                }

                setIsCopyMoveModalOpen(false)
                setIsOpen(false)
                setType('')
            },
            onError: (error) => {
                setType('')
                showGlobalSnackbar({
                    variables: {
                        message:
                            error?.message ||
                            'Error on Moving Product Listing/s',
                        variant: 'error',
                        id: `ProductCardManagementTable_BulkMoveProductListingError`,
                    },
                })
            },
        }
    )

    const [bulkDeleteProducts] = useMutation(BulkDeleteListings, {
        onCompleted: (data) => {
            const instanceId =
                data?.startBulkDeleteProductListingDetails?.instanceId

            if (!!instanceId) {
                setBulkActionProgress({ delete: instanceId })
            }
            resetPDNStore()
            setType('')
        },
        onError: (error) => {
            setType('')
            showGlobalSnackbar({
                variables: {
                    message:
                        error?.message || 'Error on Deleting Product Listing/s',
                    variant: 'error',
                    id: `ProductCardManagementTable_BulkDeleteProductListingError`,
                },
            })
        },
    })

    const handleCopyModalClose = useCallback(() => {
        setType('')
        setIsCopyMoveModalOpen(false)
        setIsOpen(false)
    }, [setIsOpen, setType])

    const handleCancelCreatePDN = useCallback(() => {
        setIsPDNModalOpen?.(false)
        setTempPDNStore({
            actionType: '',
            value: [],
        })
        setType('')
    }, [setIsPDNModalOpen])

    const handleResetAddNote = useCallback(() => {
        setIsPDNModalOpen(false)
        setType('')
    }, [])

    const handleSavePDN = useCallback(
        ({ noteType, longDescription, noteId, reason }: any) => {
            const {
                productType: { value: productType },
                specAgentProductTypeId: { value: specAgentProductTypeId },
                sectionNames: { value: sectionNames },
                isActive: { value: isActive },
                listingType: { value: listingType },
                state: { value: state },
                includeManufacturerOptions: {
                    value: includeManufacturerOptions,
                },
            } = copyMoveProductForm as Record<string, Record<string, string>> // need to cast this as different type since ts is not recognizing the form type

            const sectionData = sectionNames.split(' - ')

            const apiInput = {
                listingIds: (copyMoveParams.selectedRows as Partial<
                    ListingDetail
                >[]).map((listing: any) => listing.listingId),
                destinationProductType: productType,
                destinationProductTypeId:
                    specAgentProductTypeId &&
                    !isNaN(parseInt(specAgentProductTypeId))
                        ? parseInt(specAgentProductTypeId)
                        : null,
                destinationSectionTitle: sectionData[1],
                destinationSectionNumber: sectionData[0],
                isActive: isActive === 'Active' ? true : false,
                type: listingType,
                includeManufacturerOptions:
                    includeManufacturerOptions === 'true' ? true : false,
                newState: state,
                productDetailNote: {
                    content: longDescription,
                    productDetailNoteType: noteType,
                    reason,
                },
            }

            if (type === 'Copy') {
                bulkCopyProducts({
                    variables: {
                        input: apiInput,
                    },
                })
            } else if (type === 'Move') {
                bulkMoveProducts({
                    variables: {
                        input: apiInput,
                    },
                })
            } else if (type === 'Delete') {
                bulkDeleteProducts({
                    variables: {
                        input: {
                            listingIds: apiInput.listingIds,
                            productDetailNote: apiInput.productDetailNote,
                        },
                    },
                })
            }
        },
        [
            handleSaveNote,
            tempPDNStore,
            copyMoveParams,
            bulkCopyProducts,
            bulkMoveProducts,
            bulkDeleteProducts,
            copyMoveProductForm.sectionNames?.value,
            copyMoveProductForm.isActive?.value,
        ]
    )

    const handleDelete = useCallback(() => {
        NavPromptDialogContainer(
            JSON.stringify({
                title: 'Confirm Delete?',
                preferredMessage:
                    'You have chosen to delete the selected  product/s, Continue?',
                buttonColor: '#d3455b',
                confirmText: 'Yes',
                cancelText: 'No',
            } as NavPromptMessageProps),
            (callbackState: boolean) => {
                if (!callbackState) {
                    setType('')
                    return
                }
                setTempPDNStore({ actionType: 'DELETE', value: [] })
                setIsPDNModalOpen(true)
            }
        )
    }, [setType])

    useEffect(() => {
        if (!isCopyMoveModalOpen) {
            setIsCopyMoveModalOpen(isOpen)
        }
    }, [isOpen])

    useEffect(() => {
        if (type === 'Delete' && !isPDNModalOpen) {
            handleDelete()
        }
    }, [type])

    return (
        <>
            {!!isCopyMoveModalOpen && (
                <CopyMoveDialog
                    isLoading={!!loadingCopyProduct || !!loadingMoveProduct}
                    copyOrMoveModalOpen={isCopyMoveModalOpen}
                    productInfo={{
                        sectionName: copyMoveParams.sectionName,
                        productType: copyMoveParams.productType,
                    }}
                    setIsPDNModalOpen={setIsPDNModalOpen}
                    setTempPDNStore={setTempPDNStore}
                    handleCopyModalClose={handleCopyModalClose}
                    setCopyMoveProductForm={setCopyMoveProductForm}
                    copyMoveProductForm={copyMoveProductForm}
                    bulkActionType={type}
                    isBulkAction={true}
                />
            )}
            {!!isPDNModalOpen && (
                <AddNotesModal
                    isEditMode={false}
                    open={!!isPDNModalOpen}
                    onSave={handleSavePDN}
                    onCancel={handleCancelCreatePDN}
                    // @ts-ignore ts cannot recognize NoteType
                    noteType={
                        typeAction[
                            tempPDNStore.actionType as keyof typeof typeAction
                        ] as NoteType
                    }
                    action={
                        tempPDNStore.actionType as keyof typeof noteReasonLabels
                    }
                    resetAction={
                        [
                            'DELETE',
                            'STATUSCHANGE',
                            'LISTINGTYPECHANGE',
                        ].includes(tempPDNStore.actionType)
                            ? handleResetAddNote
                            : undefined
                    }
                />
            )}
        </>
    )
}

export default BulkCopyMoveDialog
